// Business Edit Form Component
// Feature: 005-business-administration

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, X, Upload, Trash2 } from 'lucide-react';
import { LocationPicker } from './LocationPicker';
import { buildImageUrl } from '@/services/api/images';
import type {
  BusinessWithOwner,
  BusinessProfileUpdate,
  CategoryOption,
} from '@/types/businessAdmin';

interface BusinessEditFormProps {
  business: BusinessWithOwner;
  categories: CategoryOption[];
  onSave: (updates: BusinessProfileUpdate, newPhotos: File[]) => Promise<{
    success: boolean;
    errors?: Record<string, string>;
  }>;
  onCancel: () => void;
}

export function BusinessEditForm({
  business,
  categories,
  onSave,
  onCancel,
}: BusinessEditFormProps) {
  const { t } = useTranslation('businessAdmin');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state - using simple strings to match database schema
  const [name, setName] = useState(business.data.name);
  const [description, setDescription] = useState(business.data.description);
  const [categoryId, setCategoryId] = useState(business.data.category || '');
  const [tags, setTags] = useState(business.data.tags?.join(', ') || '');
  const [latitude, setLatitude] = useState(business.data.location?.lat?.toString() || '');
  const [longitude, setLongitude] = useState(business.data.location?.lng?.toString() || '');

  // Photo state - support multiple photos including gallery
  const [primaryPhotoUrl, setPrimaryPhotoUrl] = useState(business.data.photo_url || '');
  const [additionalPhotos, setAdditionalPhotos] = useState<string[]>(business.data.photos || []);
  const [newPhotoFiles, setNewPhotoFiles] = useState<File[]>([]);
  const [newPhotoPreviews, setNewPhotoPreviews] = useState<{file: File, preview: string}[]>([]);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when business data changes (after save/refresh)
  useEffect(() => {
    setName(business.data.name);
    setDescription(business.data.description);
    setCategoryId(business.data.category || '');
    setTags(business.data.tags?.join(', ') || '');
    setLatitude(business.data.location?.lat?.toString() || '');
    setLongitude(business.data.location?.lng?.toString() || '');
    setPrimaryPhotoUrl(business.data.photo_url || '');
    setAdditionalPhotos(business.data.photos || []);
    setNewPhotoFiles([]);
    setNewPhotoPreviews([]);
  }, [business]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      newPhotoPreviews.forEach(({preview}) => URL.revokeObjectURL(preview));
    };
  }, [newPhotoPreviews]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) newErrors.name = t('edit.validation.nameRequired');
    if (!description.trim()) newErrors.description = t('edit.validation.descriptionRequired');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle photo file selection (supports multiple)
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newErrors: Record<string, string> = {};
    const validFiles: {file: File, preview: string}[] = [];

    for (const file of files) {
      // Validate size
      if (file.size > 5 * 1024 * 1024) {
        newErrors.photo = t('edit.validation.photoSize');
        continue;
      }

      // Validate type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        newErrors.photo = t('edit.validation.photoType');
        continue;
      }

      // Create preview URL
      const preview = URL.createObjectURL(file);
      validFiles.push({file, preview});
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors({ ...errors, ...newErrors });
    }

    setNewPhotoFiles(prev => [...prev, ...validFiles.map(v => v.file)]);
    setNewPhotoPreviews(prev => [...prev, ...validFiles]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove a new photo preview
  const removeNewPhoto = (index: number) => {
    const preview = newPhotoPreviews[index];
    URL.revokeObjectURL(preview.preview);
    setNewPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setNewPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Remove an existing photo (for now, just hide from UI - actual deletion would need separate API call)
  const removeExistingPhoto = (url: string) => {
    // TODO: Implement photo deletion via updateBusinessPhotos service
    // For now, just remove from display
    setAdditionalPhotos(prev => prev.filter(p => p !== url));
    if (url === primaryPhotoUrl) {
      setPrimaryPhotoUrl(additionalPhotos[0] || '');
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSaving(true);
    setErrors({});

    // Parse tags from comma-separated string
    const parsedTags = tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const updates: BusinessProfileUpdate = {
      name,
      description,
      category_id: categoryId || undefined,
      tags: parsedTags,
      location: latitude && longitude ? {
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      } : undefined,
    };

    try {
      const result = await onSave(updates, newPhotoFiles);

      if (!result.success && result.errors) {
        setErrors(result.errors);
      }
    } catch (err) {
      setErrors({ general: t('edit.validation.saveFailed') });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-neutral-200">
        <h2 className="text-2xl font-bold text-neutral-900">{t('edit.title')}</h2>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition disabled:opacity-50"
          >
            <X size={18} />
            {t('edit.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Save size={18} />
            )}
            {t('edit.save')}
          </button>
        </div>
      </div>

      {/* General Error */}
      {errors.general && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {errors.general}
        </div>
      )}

      {/* Business Name */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.basic')}</h3>
        
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('edit.fields.name')} <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.name ? 'border-red-300' : 'border-neutral-300'
            }`}
            disabled={isSaving}
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>
      </div>

      {/* Description */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.description')}</h3>
        
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('edit.fields.description')} <span className="text-red-500">*</span>
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.description ? 'border-red-300' : 'border-neutral-300'
            }`}
            disabled={isSaving}
          />
          {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
        </div>
      </div>

      {/* Location Map */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.coordinates')}</h3>
        
        <LocationPicker
          latitude={latitude ? parseFloat(latitude) : null}
          longitude={longitude ? parseFloat(longitude) : null}
          onChange={(lat, lng) => {
            setLatitude(lat.toString());
            setLongitude(lng.toString());
          }}
          disabled={isSaving}
        />
      </div>

      {/* Category */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.category')}</h3>
        
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('edit.fields.category')}
          </label>
          <select
            id="category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={isSaving}
          >
            <option value="">{t('edit.fields.selectCategory')}</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tags */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.tags')}</h3>
        
        <div>
          <label htmlFor="tags" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('edit.fields.tags')}
          </label>
          <input
            id="tags"
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder={t('edit.fields.tagsPlaceholder')}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={isSaving}
          />
          <p className="mt-1 text-sm text-neutral-500">{t('edit.fields.tagsHint')}</p>
        </div>
      </div>

      {/* Photo Gallery */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">{t('edit.sections.photo')}</h3>
        
        {/* Existing Photos Grid */}
        {(primaryPhotoUrl || additionalPhotos.length > 0) && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Primary Photo */}
            {primaryPhotoUrl && (
              <div className="relative group">
                <img
                  src={buildImageUrl(primaryPhotoUrl, 'card')}
                  alt="Primary business photo"
                  className="w-full h-32 object-cover rounded-lg"
                />
                <span className="absolute top-2 left-2 px-2 py-1 bg-primary-600 text-white text-xs rounded">
                  Primary
                </span>
                <button
                  onClick={() => removeExistingPhoto(primaryPhotoUrl)}
                  disabled={isSaving}
                  className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition opacity-0 group-hover:opacity-100 disabled:opacity-50"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}
            
            {/* Additional Photos */}
            {additionalPhotos.map((url, idx) => (
              <div key={idx} className="relative group">
                <img
                  src={buildImageUrl(url, 'card')}
                  alt={`Business photo ${idx + 2}`}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <button
                  onClick={() => removeExistingPhoto(url)}
                  disabled={isSaving}
                  className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition opacity-0 group-hover:opacity-100 disabled:opacity-50"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* New Photo Previews */}
        {newPhotoPreviews.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {newPhotoPreviews.map((item, idx) => (
              <div key={idx} className="relative group">
                <img
                  src={item.preview}
                  alt={`New photo ${idx + 1}`}
                  className="w-full h-32 object-cover rounded-lg border-2 border-primary-500"
                />
                <button
                  onClick={() => removeNewPhoto(idx)}
                  disabled={isSaving}
                  className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition disabled:opacity-50"
                >
                  <Trash2 size={14} />
                </button>
                <span className="absolute top-2 left-2 px-2 py-1 bg-green-600 text-white text-xs rounded">
                  New
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Upload Button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handlePhotoSelect}
            className="hidden"
            disabled={isSaving}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition disabled:opacity-50"
          >
            <Upload size={18} />
            {t('edit.fields.uploadPhoto')}
          </button>
          <p className="mt-1 text-sm text-neutral-500">{t('edit.fields.photoHint')}</p>
          {errors.photo && <p className="mt-1 text-sm text-red-600">{errors.photo}</p>}
        </div>
      </div>

      {/* Save hint */}
      <div className="pt-4 border-t border-neutral-200">
        <p className="text-sm text-neutral-500">
          {t('edit.saveHint')}
        </p>
      </div>
    </div>
  );
}
